import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import jsCookie from 'js-cookie'
import { navigate } from 'gatsby'
import { connect, useSelector } from 'react-redux'

import { LocalStorage, SessionStorage } from '@gymondo/frontend-core/utils'
import {
    COOKIE_NAMES,
    LOCAL_STORAGE_KEYS,
    SESSION_STORAGE_KEYS,
    LOGIN_REALMS,
} from '@gymondo/frontend-core/enums'

import { BARMER_VOUCHER } from '../checkout-package/config/channel.config'
import { ROUTES_ID, getPathUrl } from '../../../config/route'
import {
    setChannel,
    setSignupAction,
    setSignupVoucher,
    setSelectedProduct,
} from '../../../service/user'
import { openLoginModal } from '../../../service/page-header'
import PageContext from '../../../config/intl/page-context.context'
import { validate } from '../../../api/service/voucher/voucher.service'

import { SIGNUP_ACTIONS, QUERY_PARAMS } from '../../../config/definitions'

import { setAffiliate } from '../../../service/affiliate/affiliate.action'

/**
 * Redirect the user based on action
 */
export const Checkout = ({
    location,
    isLoggedIn,
    setUserChannel,
    setUserSignupAction,
    setVoucher,
    setProduct,
    signupAction,
    openHeaderLoginModal,
    setAffiliateInfo,
}) => {
    const pageContext = useContext(PageContext)

    const { hasMarketingConsent } = useSelector((state) => state.cookieConsent)

    useEffect(() => {
        const queryParams = qs.parse(location.search)
        const action = queryParams[QUERY_PARAMS.SIGNUP_ACTION] || signupAction
        const zanoxCookie =
            jsCookie.get(COOKIE_NAMES.AFFILIATE_ID) ||
            queryParams[QUERY_PARAMS.ZANOX_TOKEN_ZANPID] ||
            queryParams[QUERY_PARAMS.ZANOX_TOKEN_AWC]
        const sovendusToken = queryParams[QUERY_PARAMS.SOVENDUS_TOKEN]
        const programId = queryParams[QUERY_PARAMS.PROGRAM_ID]
        const isBarmerUser =
            jsCookie.get(COOKIE_NAMES.OAUTH2_REALM) === LOGIN_REALMS.BARMER
        const isSignupPlacementABTest =
            queryParams[QUERY_PARAMS.SIGNUP_PLACEMENT_AB_TEST] === 'true'

        if (programId) {
            SessionStorage.set(
                SESSION_STORAGE_KEYS.RECOMMENDED_PROGRAM_ID,
                programId
            )
        }
        if (zanoxCookie) {
            setAffiliateInfo(
                LOCAL_STORAGE_KEYS.SIGNUP_ZANOX,
                zanoxCookie,
                hasMarketingConsent
            )
        }

        if (sovendusToken) {
            setAffiliateInfo(
                LOCAL_STORAGE_KEYS.SIGNUP_SOVENDUS,
                sovendusToken,
                hasMarketingConsent
            )
            queryParams[QUERY_PARAMS.SOVENDUS_TOKEN] = undefined
        }

        ;(async () => {
            let urlIdTo = isSignupPlacementABTest
                ? ROUTES_ID.CHECKOUT_SIGNUP
                : ROUTES_ID.PUBLIC_PAGES_CHECKOUT_PACKAGE

            if (action && isLoggedIn) {
                setUserSignupAction(action)
                LocalStorage.set(LOCAL_STORAGE_KEYS.SIGNUP_ACTION, action)
                if (action === SIGNUP_ACTIONS.REPURCHASE) {
                    setUserChannel()
                } else if (
                    action === SIGNUP_ACTIONS.PAYMENT ||
                    action === SIGNUP_ACTIONS.REACTIVATION
                ) {
                    urlIdTo = ROUTES_ID.CHECKOUT_SUBSCRIBE
                }
            } else if (queryParams[QUERY_PARAMS.SHOW_LOGIN] === 'true') {
                openHeaderLoginModal()
            } else if (isBarmerUser) {
                try {
                    const { data: voucherResp } = await validate(BARMER_VOUCHER)

                    setVoucher(voucherResp)
                    setProduct(null)
                    LocalStorage.remove(LOCAL_STORAGE_KEYS.SELECTED_PRODUCT)

                    urlIdTo = ROUTES_ID.CHECKOUT_SUBSCRIBE
                } catch (error) {
                    // Barmer Voucher could not be validated - failing silently,
                    // users would continue to the checkout flow
                    console.error('Barmer voucher validation failed:', error)
                }
            }

            const url = getPathUrl({
                id: urlIdTo,
                lang: pageContext.locale,
                query: queryParams,
            })

            if (urlIdTo === ROUTES_ID.PUBLIC_PAGES_CHECKOUT_PACKAGE) {
                window.location.replace(url)
            } else {
                navigate(url, { replace: true })
            }
        })()
    }, [
        location,
        signupAction,
        isLoggedIn,
        setUserChannel,
        setUserSignupAction,
        openHeaderLoginModal,
        setAffiliateInfo,
        pageContext,
        setVoucher,
        setProduct,
    ])

    return <></>
}

Checkout.propTypes = {
    location: PropTypes.object.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
    signupAction: PropTypes.string,
    setUserChannel: PropTypes.func.isRequired,
    setUserSignupAction: PropTypes.func.isRequired,
    setVoucher: PropTypes.func.isRequired,
    setProduct: PropTypes.func.isRequired,
    openHeaderLoginModal: PropTypes.func.isRequired,
    setAffiliateInfo: PropTypes.func.isRequired,
}

Checkout.defaultProps = {
    signupAction: null,
}

export const mapStateToProps = ({ user }) => ({
    ...user,
})

export const mapDispatchToProps = (dispatch) => ({
    setUserChannel: (channel) => dispatch(setChannel(channel)),
    setUserSignupAction: (action) => dispatch(setSignupAction(action)),
    openHeaderLoginModal: () => dispatch(openLoginModal()),
    setAffiliateInfo: (affiliatePid, affiliateId, hasMarketingConsent) =>
        dispatch(setAffiliate(affiliatePid, affiliateId, hasMarketingConsent)),
    setVoucher: (voucher) => dispatch(setSignupVoucher(voucher)),
    setProduct: (product) => dispatch(setSelectedProduct(product)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Checkout)
